import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Droplets, Heart, Building2, Users, Target } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO
        path="/"
        description="Plateforme publicitaire innovante et solidaire. Votre marque sur des bouteilles d'eau gratuites distribuées en Île-de-France, finançant des puits d'eau via l'Association Ulker's."
      />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-emerald-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">
                    {mockData.hero.title}
                  </span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {mockData.hero.subtitle}
                </p>
              </div>
              
              <div className="flex justify-center pt-8">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                  >
                    {mockData.hero.ctaButtons[0].text}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={mockData.hero.heroImage}
                  alt="Distribution de bouteilles d'eau gratuites avec publicité solidaire en Île-de-France"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-sky-200 to-emerald-200 rounded-full blur-3xl opacity-30"></div>
              <div className="absolute -bottom-8 -left-8 w-96 h-96 bg-gradient-to-tr from-emerald-200 to-sky-200 rounded-full blur-3xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats décisives */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-emerald-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Pourquoi choisir Eau Gratuite ?
            </h2>
            <p className="text-xl text-sky-100 leading-relaxed">
              Des résultats prouvés qui transforment votre investissement publicitaire
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-white mb-2">10x</div>
              <div className="text-lg font-medium text-white mb-2">Plus d'impressions</div>
              <div className="text-sm text-sky-100">que le publipostage direct</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-white mb-2">2,50€</div>
              <div className="text-lg font-medium text-white mb-2">Moins cher</div>
              <div className="text-sm text-sky-100">par 10 impressions vs publipostage</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-white mb-2">29%</div>
              <div className="text-lg font-medium text-white mb-2">Retour sur investissement</div>
              <div className="text-sm text-sky-100">prouvé et mesurable</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-white mb-2">♾️</div>
              <div className="text-lg font-medium text-white mb-2">Impact social</div>
              <div className="text-sm text-sky-100">sauver des vies + aider la planète</div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-white text-sky-600 hover:bg-sky-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
              >
                Je veux ces résultats pour ma marque
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {mockData.howItWorks.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {mockData.howItWorks.subtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.howItWorks.steps.map((step, index) => {
              const IconComponent = {
                building: Building2,
                droplets: Droplets,
                heart: Heart
              }[step.icon];
              
              return (
                <Card key={step.id} className="relative p-6 hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-white to-sky-50/30">
                  <CardContent className="p-0">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mb-6">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <div className="absolute top-4 right-4 w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-sky-600">{step.id}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/comment-ca-marche">
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-sky-200 text-sky-600 hover:bg-sky-50 px-8 py-3 rounded-xl font-semibold transition-all duration-200 group"
              >
                En savoir plus
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Preview */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Publicité avec impact social
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {mockData.mission.mainText}
              </p>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-sky-100">
                <div className="flex items-center space-x-3 mb-3">
                  <Heart className="h-6 w-6 text-emerald-500" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {mockData.mission.partnership.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">{mockData.mission.partnership.description}</p>
                <div className="text-lg font-semibold text-emerald-600">
                  {mockData.mission.partnership.impact}
                </div>
              </div>
              <Link to="/mission">
                <Button 
                  variant="outline"
                  size="lg"
                  className="border-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 px-8 py-3 rounded-xl font-semibold transition-all duration-200 group"
                >
                  Découvrir notre impact
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {mockData.mission.images.map((image, index) => (
                  <div key={index} className="rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={image}
                      alt={index === 0 ? "Construction de puits d'eau potable en Afrique avec l'Association Ulker's" : "Impact social des campagnes publicitaires solidaires Eau Gratuite"}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">
              Prêt à promouvoir votre marque ?
            </h2>
            <p className="text-xl text-sky-100 leading-relaxed">
              Lancez votre campagne publicitaire solidaire dès aujourd'hui et générez un impact social positif.
            </p>
            <div className="flex justify-center">
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-white text-sky-600 hover:bg-sky-50 px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                >
                  Promouvoir votre marque
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;